import {
  ANBIMA_REPORT_FORM_TO_API_MAP,
  ANBIMA_REPORT_API_TO_FORM_MAP,
} from '@modules/clients/constants/Text.js'
import { dataWirecard, dataWirecardReverse } from '@utils/Format'

const trimString = (v) => (v == null ? null : String(v).trim())

export const convertSimNaoToBoolean = (value) => {
  if (value === 'Sim') return true
  if (value === 'Não') return false
  return null
}

export const arrayToCommaString = (list) => {
  if (Array.isArray(list) && list.length > 0) {
    return list.join(', ')
  }
  return null
}

export const convertDateFormat = (dateString) => {
  if (!dateString) return null
  if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) return dateString
  return dataWirecard(dateString)
}

export const convertPercentageToDecimal = (value, tipo) => {
  if (value === null || value === undefined) return null
  if (tipo !== 'Percentual') return parseFloat(value)
  return parseFloat(value) / 100
}

export const convertBooleanToSimNao = (value) => {
  if (value === true) return 'Sim'
  if (value === false) return 'Não'
  return null
}

export const commaStringToArray = (str) => {
  if (!str) return null
  return String(str)
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)
}

export const convertDateToFormFormat = (dateString) => {
  if (!dateString) return null
  if (dateString.match(/^\d{2}\/\d{2}\/\d{4}$/)) return dateString
  return dataWirecardReverse(dateString)
}

export const convertDecimalToPercentage = (value, tipo) => {
  if (value === null || value === undefined) return null
  if (tipo === 'Percentual') return parseFloat(value) * 100
  return parseFloat(value)
}

const apiTransformations = {
  codigo: (value) => trimString(value),
  primeiroAporte: (value) => convertDateFormat(value),
  encerramentoContrato: (value) => convertDateFormat(value),
  creditoPrivado: (value) => convertSimNaoToBoolean(value),
  investimentoExterior: (value) => convertSimNaoToBoolean(value),
  investimentoCotasFundos: (value) => convertSimNaoToBoolean(value),
  derivativos: (value) => convertSimNaoToBoolean(value),
  utilizaBenchmark: (value) => convertSimNaoToBoolean(value),
  custodianteContratado: (value) => convertSimNaoToBoolean(value),
  cobrancaTaxaComplementar: (value) => convertSimNaoToBoolean(value) || false,
  cobrancaTaxaPerformance: (value) => convertSimNaoToBoolean(value) || false,
  controladorContratado: (value) => convertSimNaoToBoolean(value) || false,
  estrategiasDerivativos: (value) => arrayToCommaString(value),
  benchmark: (value) => arrayToCommaString(value),
  valorTaxaGestao: (value, formData) =>
    convertPercentageToDecimal(value, formData.tipoTaxaGestao),
  valorTaxaComplementar: (value, formData) =>
    convertPercentageToDecimal(value, formData.tipoCobrancaTaxaComplementar),
  valorTaxaPerformance: (value, formData) =>
    convertPercentageToDecimal(value, formData.tipoCobrancaTaxaPerformance),
  rentabilidadeTir: (value) => convertPercentageToDecimal(value, 'Percentual'),
  aportes: (value) => (value != null ? parseFloat(value) : null),
  resgates: (value) => (value != null ? parseFloat(value) : null),
  volumeFinanceiro: (value) => (value != null ? parseFloat(value) : null),
  valorAplicadoCotas: (value) => (value != null ? parseFloat(value) : null),
}

const formTransformations = {
  codigo_anbima: (value) => trimString(value),
  data_primeiro_aporte: (value) => convertDateToFormFormat(value),
  data_encerramento_contrato: (value) => convertDateToFormFormat(value),
  permite_credito_privado: (value) => convertBooleanToSimNao(value),
  permite_investimento_exterior: (value) => convertBooleanToSimNao(value),
  permite_investimento_cotas: (value) => convertBooleanToSimNao(value),
  permite_derivativos: (value) => convertBooleanToSimNao(value),
  utiliza_benchmark: (value) => convertBooleanToSimNao(value),
  custodiante_contratado: (value) => convertBooleanToSimNao(value),
  cobranca_taxa_complementar: (value) => convertBooleanToSimNao(value),
  cobranca_taxa_performance: (value) => convertBooleanToSimNao(value),
  controlador_contratado: (value) => convertBooleanToSimNao(value),
  estrategias_derivativos: (value) => commaStringToArray(value),
  benchmark_aplicavel: (value) => commaStringToArray(value),
  valor_taxa_gestao: (value, apiData) =>
    convertDecimalToPercentage(value, apiData.tipo_taxa_gestao),
  valor_taxa_complementar: (value, apiData) =>
    convertDecimalToPercentage(value, apiData.tipo_taxa_complementar),
  valor_taxa_performance: (value, apiData) =>
    convertDecimalToPercentage(value, apiData.tipo_taxa_performance),
  rentabilidade_tir: (value) => convertDecimalToPercentage(value, 'Percentual'),
}

export const mapAnbimaReportFieldsToAPI = (formData, mapping) => {
  const result = {}
  for (const [formField, apiField] of Object.entries(mapping)) {
    if (apiField === null) continue
    let value = formData[formField]
    const transform = apiTransformations[formField]
    if (transform) value = transform(value, formData)
    result[apiField] = value
  }
  return result
}

export const mapAnbimaReportFieldsToForm = (apiData, mapping) => {
  const result = {}
  for (const [apiField, formField] of Object.entries(mapping)) {
    let value = Object.prototype.hasOwnProperty.call(apiData, apiField)
      ? apiData[apiField]
      : null
    const transform = formTransformations[apiField]
    if (transform) value = transform(value, apiData)
    result[formField] = value === undefined ? null : value
  }
  return result
}

export const mapAnbimaReportFields = (source, mapping) => {
  const isToAPI = mapping === ANBIMA_REPORT_FORM_TO_API_MAP
  const isToForm = mapping === ANBIMA_REPORT_API_TO_FORM_MAP
  if (isToAPI) return mapAnbimaReportFieldsToAPI(source, mapping)
  if (isToForm) return mapAnbimaReportFieldsToForm(source, mapping)
  const result = {}
  for (const [sourceKey, targetKey] of Object.entries(mapping)) {
    result[targetKey] =
      source[sourceKey] !== undefined ? source[sourceKey] : null
  }
  return result
}
